import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { getStateCodeFromZipCode } from "@redotech/redo-web/state-code";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { createContext, memo, ReactNode, useMemo, useState } from "react";
import * as z from "zod/v4";
import { useLocationManager } from "../../location";

const ZIP_STORAGE_KEY = "redo-zip";
const ZIP_EXPIRY_HOURS = 24; // 1 day expiry

const zipStorageParser = z.union([
  z.object({ success: z.literal(true), zipCode: z.string().min(3) }),
  z.object({
    success: z.literal(false),
    error: z.string(),
    outsideUnitedStates: z.boolean().optional(),
  }),
]);
type ZipStorage = z.infer<typeof zipStorageParser>;

interface ZipCodeContextValue {
  outsideUnitedStates: boolean | undefined;
  savedZipCode: string | undefined;
  saveZipCode: (
    zipCode: string,
  ) => { success: true } | { success: false; error: string };
}

export const ZipCodeContext = createContext<ZipCodeContextValue | null>(null);

export const ZipCodeProvider = memo(function ZipCodeProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const locationManager = useLocationManager();

  const [savedZipCode, setSavedZipCode] = useState<string | undefined>(
    undefined,
  );
  const [outsideUnitedStates, setOutsideUnitedStates] = useState<
    boolean | undefined
  >(undefined);

  const validateAndSaveZipStorage = (zipStorage: ZipStorage) => {
    // Ensure we don't store invalid zip storage objects
    try {
      zipStorageParser.parse(zipStorage);
    } catch (error) {
      console.error(error);
      return;
    }

    setLocalStorageWithExpiry(ZIP_STORAGE_KEY, zipStorage, ZIP_EXPIRY_HOURS);
  };

  // Save zip code to localStorage and update state
  const saveZipCode = useHandler((zipCode: string) => {
    if (!getStateCodeFromZipCode(zipCode)) {
      // Don't save the invalid zip code
      return { success: false, error: "Invalid zip code" };
    }

    const zipStorage: ZipStorage = { success: true, zipCode };
    validateAndSaveZipStorage(zipStorage);

    setSavedZipCode(zipStorage.zipCode);

    return zipStorage;
  });

  // Load initial zip code from localStorage or location manager
  useLoad(
    async (signal) => {
      const storedZip = getLocalStorageWithExpiry(ZIP_STORAGE_KEY);
      const zipParsed = zipStorageParser.safeParse(storedZip);
      if (zipParsed.success) {
        if (!zipParsed.data.success) {
          setOutsideUnitedStates(zipParsed.data.outsideUnitedStates);
          return;
        }

        // We got the zip from localStorage, so we don't need to use the saveZipCode wrapper
        setSavedZipCode(zipParsed.data.zipCode);
        return;
      }

      const locDetails = await locationManager.getDetailedLocation(signal);
      if (locDetails.country !== "US") {
        validateAndSaveZipStorage({
          success: false,
          error: "Invalid country",
          outsideUnitedStates: true,
        });
        setOutsideUnitedStates(true);
        return;
      }

      // Assumption: the location manager will always return a zip code
      // from which we can get the state code if the country is "US"
      saveZipCode(locDetails.postalCode);
    },
    [locationManager],
  );

  const zipCodeState: ZipCodeContextValue = useMemo(
    () => ({ outsideUnitedStates, savedZipCode, saveZipCode }),
    [outsideUnitedStates, savedZipCode, saveZipCode],
  );

  return (
    <ZipCodeContext.Provider value={zipCodeState}>
      {children}
    </ZipCodeContext.Provider>
  );
});
